import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Statistic, Select, Table, Tag, Space, Typography, Spin, Empty, Progress, Divider,
  message, Timeline,
} from 'antd';
import {
  FunnelPlotOutlined, TeamOutlined, GlobalOutlined, MailOutlined,
  RobotOutlined, FormOutlined, LineChartOutlined, UserOutlined,
} from '@ant-design/icons';
import { funnelAPI, scenarioAPI } from '../services/api';

const { Title, Text } = Typography;

const EVENT_COLORS = {
  page_visit: '#1890ff',
  form_submit: '#52c41a',
  email_sent: '#faad14',
  email_opened: '#13c2c2',
  email_clicked: '#722ed1',
  chat_started: '#eb2f96',
  chat_completed: '#f5222d',
};

const EVENT_LABELS = {
  page_visit: '頁面瀏覽',
  form_submit: '表單提交',
  email_sent: '郵件發送',
  email_opened: '郵件開啟',
  email_clicked: '郵件點擊',
  chat_started: '聊天開始',
  chat_completed: '聊天完成',
};

export default function FunnelDashboardPage() {
  const [summary, setSummary] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [timeSeries, setTimeSeries] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await funnelAPI.getSummary();
      setSummary(res.data);
    } catch (e) {
      console.error('Summary fetch failed:', e);
    }
  }, []);

  const fetchScenarios = useCallback(async () => {
    try {
      const res = await scenarioAPI.list({});
      setScenarios(res.data || []);
    } catch (e) {
      console.error('Scenarios fetch failed:', e);
    }
  }, []);

  const fetchFunnel = useCallback(async (scenarioId) => {
    if (!scenarioId) { setFunnel(null); return; }
    try {
      const res = await funnelAPI.getFunnel(scenarioId);
      setFunnel(res.data);
    } catch (e) {
      console.error('Funnel fetch failed:', e);
    }
  }, []);

  const fetchTimeSeries = useCallback(async (scenarioId) => {
    try {
      const params = scenarioId ? { scenario_id: scenarioId } : {};
      const res = await funnelAPI.getTimeSeries(params);
      setTimeSeries(res.data || []);
    } catch (e) {
      console.error('Time series fetch failed:', e);
    }
  }, []);

  const fetchRecentEvents = useCallback(async (scenarioId) => {
    try {
      const params = scenarioId ? { scenario_id: scenarioId, limit: 30 } : { limit: 30 };
      const res = await funnelAPI.getEvents(params);
      setRecentEvents(res.data || []);
    } catch (e) {
      console.error('Events fetch failed:', e);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSummary(), fetchScenarios(), fetchTimeSeries(), fetchRecentEvents()])
      .finally(() => setLoading(false));
  }, [fetchSummary, fetchScenarios, fetchTimeSeries, fetchRecentEvents]);

  const handleScenarioChange = (id) => {
    setSelectedScenario(id);
    fetchFunnel(id);
    fetchTimeSeries(id);
    fetchRecentEvents(id);
  };

  if (loading && !summary) {
    return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  }

  return (
    <div>
      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={6} lg={3}>
          <Card size="small">
            <Statistic title="行銷情境" value={summary?.total_scenarios || 0} prefix={<FunnelPlotOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={3}>
          <Card size="small">
            <Statistic title="聯絡人" value={summary?.total_contacts || 0} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={3}>
          <Card size="small">
            <Statistic title="Landing Pages" value={summary?.total_landing_pages || 0} prefix={<GlobalOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={3}>
          <Card size="small">
            <Statistic title="郵件活動" value={summary?.total_campaigns || 0} prefix={<MailOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={3}>
          <Card size="small">
            <Statistic title="Chatbots" value={summary?.total_chatbots || 0} prefix={<RobotOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={3}>
          <Card size="small">
            <Statistic title="表單提交" value={summary?.total_form_submissions || 0} prefix={<FormOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={3}>
          <Card size="small">
            <Statistic title="郵件發送" value={summary?.total_emails_sent || 0} prefix={<MailOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={3}>
          <Card size="small">
            <Statistic title="聊天會話" value={summary?.total_chat_sessions || 0} prefix={<RobotOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Scenario Selector */}
      <Card style={{ marginBottom: 24 }}>
        <Space>
          <Text strong>選擇情境：</Text>
          <Select
            style={{ width: 300 }}
            placeholder="選擇行銷情境查看漏斗"
            allowClear
            value={selectedScenario}
            onChange={handleScenarioChange}
            options={scenarios.map(s => ({ value: s.id, label: `#${s.id} ${s.name}` }))}
          />
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        {/* Funnel Visualization */}
        <Col xs={24} lg={14}>
          <Card title={<Space><FunnelPlotOutlined /> 行銷漏斗</Space>}>
            {funnel ? (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>{funnel.scenario_name}</Text>
                  <Text type="secondary" style={{ marginLeft: 12 }}>
                    總觸及聯絡人：{funnel.total_contacts}
                  </Text>
                </div>
                {funnel.steps.length === 0 ? (
                  <Empty description="此情境尚無漏斗數據" />
                ) : (
                  funnel.steps.map((step, idx) => {
                    const maxCount = Math.max(...funnel.steps.map(s => s.count), 1);
                    const widthPct = Math.max((step.count / maxCount) * 100, 10);
                    return (
                      <div key={idx} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text>{step.step_name}</Text>
                          <Space>
                            <Tag color="blue">{step.count}</Tag>
                            <Text type="secondary">{step.conversion_rate}%</Text>
                          </Space>
                        </div>
                        <div style={{
                          height: 32,
                          width: `${widthPct}%`,
                          background: `linear-gradient(90deg, #1890ff, #36cfc9)`,
                          borderRadius: 4,
                          transition: 'width 0.5s ease',
                          margin: '0 auto',
                        }} />
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <Empty description="請選擇情境查看漏斗" />
            )}
          </Card>
        </Col>

        {/* Time Series Summary */}
        <Col xs={24} lg={10}>
          <Card title={<Space><LineChartOutlined /> 事件趨勢 (30天)</Space>}>
            {timeSeries.length === 0 ? (
              <Empty description="暫無數據" />
            ) : (
              timeSeries.map((series) => {
                const total = series.data.reduce((sum, p) => sum + p.count, 0);
                return (
                  <div key={series.event_type} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Tag color={EVENT_COLORS[series.event_type] || '#999'}>
                        {EVENT_LABELS[series.event_type] || series.event_type}
                      </Tag>
                      <Text strong>{total}</Text>
                    </div>
                    <Progress
                      percent={100}
                      strokeColor={EVENT_COLORS[series.event_type] || '#999'}
                      size="small"
                      format={() => `${series.data.length} 天有數據`}
                    />
                  </div>
                );
              })
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent Events */}
      <Card title="最近事件" style={{ marginTop: 16 }}>
        <Table
          dataSource={recentEvents}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 15 }}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 60 },
            {
              title: '事件類型', dataIndex: 'event_type', width: 120,
              render: (v) => (
                <Tag color={EVENT_COLORS[v] || '#999'}>
                  {EVENT_LABELS[v] || v}
                </Tag>
              ),
            },
            { title: '步驟類型', dataIndex: 'step_type', width: 100 },
            { title: '來源', dataIndex: 'source_type', width: 100 },
            { title: '情境 ID', dataIndex: 'scenario_id', width: 80 },
            { title: '聯絡人 ID', dataIndex: 'contact_id', width: 90 },
            {
              title: '時間', dataIndex: 'created_at', width: 160,
              render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-',
            },
          ]}
        />
      </Card>
    </div>
  );
}
